import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";

export const Footer = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("Welcome. Look for the next dispatch.");
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="mt-32 border-t border-white/5 bg-[#0A0A0A]">
            <div className="container-rail py-20 grid gap-16 md:grid-cols-12">
                <div className="md:col-span-5 space-y-6">
                    <div className="text-overline text-[#A3A3A3]">{t("footer.dispatch_label")}</div>
                    <h3 className="text-display text-4xl sm:text-5xl">
                        {t("footer.newsletter_title_1")} <span className="text-display-italic">{t("footer.newsletter_title_italic")}</span>
                    </h3>
                    <p className="text-[#A3A3A3] max-w-md leading-relaxed">
                        {t("footer.newsletter_body")}
                    </p>
                    <form onSubmit={onSubmit} data-testid="newsletter-form" className="flex items-stretch border border-white/10 max-w-md">
                        <input
                            type="email"
                            required
                            placeholder={t("footer.newsletter_placeholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="newsletter-email-input"
                            className="flex-1 bg-transparent px-4 py-3 text-sm text-[#F4F4F0] placeholder:text-[#666] focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="newsletter-submit"
                            className="bg-[#EDEDE6] px-5 text-[10px] tracking-[0.22em] uppercase text-[#0C0C0C] font-display disabled:opacity-50"
                        >
                            {loading ? "..." : t("footer.newsletter_submit")}
                        </button>
                    </form>
                </div>

                <div className="md:col-span-2 md:col-start-7 space-y-4">
                    <div className="text-overline text-[#666]">{t("footer.catalogue_label")}</div>
                    <ul className="space-y-2.5 text-sm text-[#A3A3A3]">
                        <li><Link to="/shop/women" className="hover:text-[#F4F4F0]">{t("nav.women")}</Link></li>
                        <li><Link to="/shop/men" className="hover:text-[#F4F4F0]">{t("nav.men")}</Link></li>
                        <li><Link to="/shop/fragrance" className="hover:text-[#F4F4F0]">{t("nav.fragrance")}</Link></li>
                        <li><Link to="/shop/women?c=accessories" className="hover:text-[#F4F4F0]">{t("footer.accessories", { defaultValue: "Accessories" })}</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2 space-y-4">
                    <div className="text-overline text-[#666]">{t("footer.studio_label")}</div>
                    <ul className="space-y-2.5 text-sm text-[#A3A3A3]">
                        <li><Link to="/about" className="hover:text-[#F4F4F0]">{t("footer.about")}</Link></li>
                        <li><Link to="/journal" className="hover:text-[#F4F4F0]">{t("footer.journal")}</Link></li>
                        <li><Link to="/lookbook" className="hover:text-[#F4F4F0]">{t("footer.lookbook")}</Link></li>
                        <li><Link to="/stockists" className="hover:text-[#F4F4F0]">{t("footer.stockists")}</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2 space-y-4">
                    <div className="text-overline text-[#666]">{t("footer.follow_label")}</div>
                    <div className="flex gap-3">
                        <a href="#" className="border border-white/10 p-2.5 hover:border-white/40 transition-colors" aria-label="Instagram"><Instagram className="h-4 w-4" strokeWidth={1.25} /></a>
                        <a href="#" className="border border-white/10 p-2.5 hover:border-white/40 transition-colors" aria-label="Twitter"><Twitter className="h-4 w-4" strokeWidth={1.25} /></a>
                        <a href="#" className="border border-white/10 p-2.5 hover:border-white/40 transition-colors" aria-label="Youtube"><Youtube className="h-4 w-4" strokeWidth={1.25} /></a>
                    </div>
                    <a
                        href="https://wa.me/12428016950?text=Hello%20SAVNT%20%E2%80%94%20I%27d%20like%20to%20enquire%20about%20a%20piece."
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-link"
                        className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#25D366] transition-colors pt-2"
                    >
                        <MessageCircle className="h-4 w-4" strokeWidth={1.25} />
                        {t("footer.whatsapp_us")}
                    </a>
                </div>
            </div>

            <div className="border-t border-white/5">
                <div className="container-rail py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] tracking-[0.22em] uppercase text-[#666] font-display">
                    <div>{t("footer.copyright")}</div>
                    <div className="flex gap-6">
                        <span>{t("footer.privacy")}</span>
                        <span>{t("footer.terms")}</span>
                        <span>{t("footer.care")}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};
